import { Component, Inject, OnInit } from "@angular/core";
import {
	FormBuilder,
	FormControl,
	FormGroup,
	Validators,
} from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ToastService } from "angular-toastify";
import { AdventureProgram } from "src/app/shared/models/adventure-program.model";
import { AdventureProgramStore } from "src/app/shared/services/stores/adventure-program.store.service";
import { VALID_AGES, VALID_ARCADE_TYPES } from "src/app/shared/constants";
import { VALID_PROGRAM_DURATIONS } from "src/app/shared/models/programs.model";
import { AdventureService } from "src/app/shared/services/adventure.service";
import { Adventure } from "src/app/shared/models/adventure.model";
import { ImageUploaderService } from "src/app/shared/services/image-uploader.service";
import { ImageFiles } from "src/app/shared/models/image-upload.model";
import { MatCheckboxChange } from "@angular/material/checkbox";

@Component({
	selector: "app-add-adventure-programs",
	templateUrl: "./add-adventure-programs.component.html",
	styleUrls: ["./add-adventure-programs.component.scss"],
})
export class AddAdventureProgramsDialog implements OnInit {
	programForm: FormGroup;
	loading = false;

	ageOptions = VALID_AGES;
	durationOptions = VALID_PROGRAM_DURATIONS;
	arcadeTypeOptions = VALID_ARCADE_TYPES;

	isEditMode = false;

	availableAdventures: Adventure[] = [];
	selectedAdventures: Adventure[] = [];

	filteredTags: string[] = [];
	filteredAdventures: Adventure[] = [];

	adventuresLoading = false;

	customArcadeType = false;
	customArcadeControl = new FormControl('');

	imageControl = new FormControl("", Validators.required);
	toVerify = false;
	imageVerify = '';
	files: ImageFiles[] = [];

	isPublished = false;

	priorityLabels = ['Very Low Priority', 'Low Priority', 'Normal Priority', 'High Priority', 'Very High Priority'];
	priorityColors = ['#8B0000', '#FF0000', '#FFD700', '#90EE90', '#006400'];
	selectedPriority = 2; // Default to Normal Priority

	constructor(
		private fb: FormBuilder,
		private dialogRef: MatDialogRef<AddAdventureProgramsDialog>,
		private adventureProgramStore: AdventureProgramStore,
		private adventureService: AdventureService,
		private toastService: ToastService,
		private uploadService: ImageUploaderService,
		@Inject(MAT_DIALOG_DATA) public data: { program?: AdventureProgram }
	) {
		this.programForm = this.fb.group({
			name: ["", Validators.required],
			description: ["", Validators.required],
			image: [""],
			ages: [[], Validators.required],
			arcadeType: ["homework", Validators.required],
			isPublished: [false],
			adventureIds: [[]],
			priority: [2], // Default to Normal Priority
		});
	}

	formatArcadeType(arcadeType: string): string {
		return arcadeType
			.split('_')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	unformatArcadeType(arcadeType: string): string {
		return arcadeType.toLowerCase().replace(/\s+/g, '_');
	}

	onArcadeTypeChange(value: string): void {
		this.customArcadeType = value === 'custom';
		this.programForm.get('arcadeType')?.setValue(value);
	}

	onCustomArcadeTypeChange(value: string): void {
		this.customArcadeControl.setValue(value);
	}

	ngOnInit(): void {
		this.loadAdventures();

		if (this.data?.program) {
			this.isEditMode = true;
			const programData = {
				...this.data.program,
			};

			this.isPublished = this.data.program.isPublished;

			if (!VALID_ARCADE_TYPES.includes(this.data.program.arcadeType as typeof VALID_ARCADE_TYPES[number])) {
				this.customArcadeType = true;
				this.customArcadeControl.setValue(this.formatArcadeType(this.data.program.arcadeType));
				programData.arcadeType = 'custom';
			}

			this.selectedPriority = this.data.program.priority;

			this.programForm.patchValue(programData);

			if (this.data.program.image) {
				this.setImage(this.data.program.image);
				this.verifyImage();
			}
		}

		document.documentElement.style.setProperty('--priority-color', this.priorityColors[this.selectedPriority]);
		document.documentElement.style.setProperty('--value', (this.selectedPriority / 4 * 100).toString());
	}

	private loadAdventures(): void {
		this.adventuresLoading = true;
		this.adventureService.getLiveAdventures().subscribe({
			next: (adventures) => {
				this.availableAdventures = adventures || [];
				this.filteredAdventures = this.availableAdventures;
				if (this.isEditMode && this.data?.program?.adventureIds) {
					const program = this.data.program;
					if (program) {
						this.selectedAdventures = this.availableAdventures.filter(d =>
							program.adventureIds.includes(d.uid)
						);
					}
				}
				this.adventuresLoading = false;
			},
			error: (error) => {
				console.error("Error loading adventures:", error);
				this.toastService.error("Failed to load adventures");
				this.adventuresLoading = false;
			},
		});
	}

	onAdventureSelected(adventure: Adventure): void {
		const index = this.selectedAdventures.findIndex(
			(d) => d.uid === adventure.uid
		);
		if (index === -1) {
			this.selectedAdventures.push(adventure);
		} else {
			this.selectedAdventures.splice(index, 1);
		}
		this.programForm.patchValue({
			adventureIds: this.selectedAdventures.map((d) => d.uid),
		});
	}

	toggleAdventure(adventure: Adventure): void {
		const index = this.selectedAdventures.findIndex(d => d.uid === adventure.uid);
		if (index === -1) {
			this.selectedAdventures.push(adventure);
		} else {
			this.selectedAdventures.splice(index, 1);
		}
	}

	isAdventureSelected(adventure: Adventure): boolean {
		return this.selectedAdventures.some(d => d.uid === adventure.uid);
	}

	resetImage(): void {
		this.files = [];
		this.imageVerify = '';
		this.toVerify = false;
	}

	verifyImage(): void {
		this.imageVerify = this.imageControl.value;
		this.toVerify = true;
	}

	setFile(file: ImageFiles | null): void {
		if (file === null) {
			this.imageControl.setValue('');
			this.files = [];
			return;
		}
		this.imageControl.setValue(file.name);
		this.files.push(file);
	}

	setImage(imgId: string): void {
		this.imageControl.setValue(imgId);
	}

	onPublishChange(event: MatCheckboxChange) {
		this.isPublished = event.checked;
	}

	onSubmit(): void {
		if (this.programForm.valid) {
			if (this.selectedAdventures.length === 0) {
				this.toastService.error('Please select at least one adventure');
				return;
			}

			this.loading = true;
			const formValue = this.programForm.value;
			const programData = {
				...formValue,
				adventureIds: this.selectedAdventures.map(d => d.uid),
				arcadeType: formValue.arcadeType === 'custom' ? this.unformatArcadeType(this.customArcadeControl.value) : this.unformatArcadeType(formValue.arcadeType),
				isPublished: this.isPublished,
			};

			if (this.files.length > 0) {
				const uploadFileIds = this.files.map(file => ({
					id: file.name,
					extension: 'jpg',
				}));

				const fileBlobs = this.files.map(fileInfo => {
					const imageBlob = new Blob([fileInfo.file], { type: `image/jpeg` });
					return imageBlob;
				});

				this.uploadService.uploadImages(fileBlobs, uploadFileIds).subscribe({
					next: (res) => {
						if (!res) {
							this.loading = false;
							this.toastService.error('Failed to upload image');
							return;
						}
						this.toastService.success('Image was uploaded successfully');
						programData.image = this.files[0].name;
						this.saveProgram(programData);
					},
					error: () => {
						this.loading = false;
						this.toastService.error('Failed to upload image');
					}
				});
			} else if (this.imageControl.value) {
				programData.image = this.imageControl.value;
				this.saveProgram(programData);
			} else {
				this.loading = false;
				this.toastService.error('Image is required');
			}
		}
	}

	private saveProgram(programData: any): void {
		if (this.isEditMode && this.data?.program) {
			this.adventureProgramStore.updateProgram(this.data.program.uid, programData);
		} else {
			this.adventureProgramStore.createProgram(programData);
		}
		this.dialogRef.close(true);
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	filterAdventures(event: Event): void {
		const filterValue = (event.target as HTMLInputElement).value.toLowerCase();
		this.filteredAdventures = this.availableAdventures.filter(adventure =>
			adventure.name.toLowerCase().includes(filterValue)
		);
	}

	removeAdventure(index: number): void {
		this.selectedAdventures.splice(index, 1);
		this.programForm.patchValue({
			adventureIds: this.selectedAdventures.map(d => d.uid),
		});
	}

	onPriorityChange(value: string): void {
		const numValue = parseInt(value, 10);
		this.selectedPriority = numValue;
		this.programForm.patchValue({ priority: numValue });
		document.documentElement.style.setProperty('--priority-color', this.priorityColors[numValue]);
		document.documentElement.style.setProperty('--value', (numValue / 4 * 100).toString());
	}
}
